import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import store from "@/stores/store";

function localTodayISO() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

const DEFAULT_FILTERS = {
    planned_date: localTodayISO(),
    establishment: "all",
    mode: "todo",
    assigned_to_me: false,
    unassigned: false,
    search: "",
    offset: 0,
    limit: 50,
};

const state = {
    requests: [],
    requests_count: 0,
    requests_filters: { ...DEFAULT_FILTERS },
    stats: {
        todo_count: 0,
        planned_units: 0,
        in_progress_count: 0,
        produced_units: 0,
        planned_units_done: 0,
    },
    requests_loading: false,
    stats_loading: false,
    requests_errors: null,
    user_establishments: [],
    user_establishments_loading: false,
    detail: null,
    detail_loading: false,
    confirm_preview: null,
    confirm_preview_loading: false,
    schedule_saving: false,
    pending_request_action: null,
};

const mutations = {
    updateRequests(state, requests) {
        state.requests = requests;
    },
    updateRequestsCount(state, count) {
        state.requests_count = count;
    },
    updateRequestsFilters(state, filters) {
        state.requests_filters = { ...state.requests_filters, ...filters };
    },
    updateStats(state, stats) {
        state.stats = stats;
    },
    updateRequestsLoading(state, loading) {
        state.requests_loading = loading;
    },
    updateStatsLoading(state, loading) {
        state.stats_loading = loading;
    },
    updateRequestsErrors(state, errors) {
        state.requests_errors = errors;
    },
    updateUserEstablishments(state, establishments) {
        state.user_establishments = establishments;
    },
    updateUserEstablishmentsLoading(state, loading) {
        state.user_establishments_loading = loading;
    },
    updateDetail(state, detail) {
        state.detail = detail;
    },
    updateDetailLoading(state, loading) {
        state.detail_loading = loading;
    },
    updateConfirmPreview(state, preview) {
        state.confirm_preview = preview;
    },
    updateConfirmPreviewLoading(state, loading) {
        state.confirm_preview_loading = loading;
    },
    updateScheduleSaving(state, saving) {
        state.schedule_saving = saving;
    },
    updatePendingRequestAction(state, pending) {
        state.pending_request_action = pending;
    },
    resetFilters(state) {
        state.requests_filters = { ...DEFAULT_FILTERS };
    },
};

function buildListParams(filters) {
    const params = {
        limit: filters.limit,
        offset: filters.offset,
        mode: filters.mode || "todo",
    };
    if (filters.planned_date) {
        params.planned_date = filters.planned_date;
    }
    if (filters.establishment && filters.establishment !== "all") {
        params.establishment = filters.establishment;
    }
    if (filters.assigned_to_me) {
        params.assigned_to_me = true;
    }
    if (filters.unassigned) {
        params.unassigned = true;
    }
    if (filters.search) {
        params.search = filters.search;
    }
    return params;
}

function buildStatsParams(filters) {
    const params = {};
    if (filters.planned_date) {
        params.planned_date = filters.planned_date;
    }
    if (filters.establishment && filters.establishment !== "all") {
        params.establishment = filters.establishment;
    }
    return params;
}

const actions = {
    initFromRoute({ commit, dispatch, state }, route) {
        const query = route?.query || {};
        const today = localTodayISO();

        commit("updateRequestsFilters", {
            planned_date: query.date || today,
            establishment: query.establishment || "all",
            mode: query.mode || "todo",
            assigned_to_me: query.assigned_to_me === "true",
            unassigned: query.unassigned === "true",
            search: query.search || "",
        });

        if (state.user_establishments.length || state.user_establishments_loading) {
            return Promise.resolve();
        }
        return dispatch("fetchUserEstablishments");
    },

    async fetchUserEstablishments({ commit }) {
        commit("updateUserEstablishmentsLoading", true);
        try {
            const response = await axios.get("/api/productionrequests/establishments/");
            const establishments = response.data || [];
            commit("updateUserEstablishments", establishments);
            return establishments;
        } catch (xhr_error) {
            commit("updateUserEstablishments", []);
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateUserEstablishmentsLoading", false);
        }
    },

    async fetchRequests({ commit, state }) {
        commit("updateRequestsLoading", true);
        commit("updateRequestsErrors", null);
        try {
            const response = await axios.get("/api/productionrequests/", {
                params: buildListParams(state.requests_filters),
            });
            commit("updateRequests", response.data.results || response.data);
            commit("updateRequestsCount", response.data.count || 0);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateRequestsErrors", error);
            commit("updateRequests", []);
            commit("updateRequestsCount", 0);
            throw error;
        } finally {
            commit("updateRequestsLoading", false);
        }
    },

    async fetchStats({ commit, state }) {
        commit("updateStatsLoading", true);
        try {
            const response = await axios.get("/api/productionrequests/stats/", {
                params: buildStatsParams(state.requests_filters),
            });
            commit("updateStats", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateStatsLoading", false);
        }
    },

    async fetchDetail({ commit }, id) {
        commit("updateDetailLoading", true);
        try {
            const response = await axios.get(`/api/productionrequests/${id}/`);
            commit("updateDetail", response.data);
            return response.data;
        } catch (xhr_error) {
            commit("updateDetail", null);
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateDetailLoading", false);
        }
    },

    async fetchConfirmPreview({ commit }, { id, produced_quantity }) {
        commit("updateConfirmPreviewLoading", true);
        try {
            const response = await axios.get(
                `/api/productionrequests/${id}/confirm-preview/`,
                { params: { produced_quantity } }
            );
            commit("updateConfirmPreview", response.data);
            return response.data;
        } catch (xhr_error) {
            commit("updateConfirmPreview", null);
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateConfirmPreviewLoading", false);
        }
    },

    async previewSchedule({ commit }, payload) {
        const response = await axios.post(
            "/api/productionrequests/preview-schedule/",
            payload
        );
        return response.data;
    },

    async schedule({ commit, dispatch }, payload) {
        commit("updateScheduleSaving", true);
        try {
            const response = await axios.post(
                "/api/productionrequests/schedule/",
                payload
            );
            await Promise.all([dispatch("fetchRequests"), dispatch("fetchStats")]);
            store.dispatch("session/fetchStats").catch(() => {});
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateScheduleSaving", false);
        }
    },

    async assignMe({ commit, dispatch }, id) {
        commit("updatePendingRequestAction", { id, action: "assign" });
        try {
            const response = await axios.post(
                `/api/productionrequests/${id}/assign-me/`
            );
            await Promise.all([dispatch("fetchRequests"), dispatch("fetchStats")]);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePendingRequestAction", null);
        }
    },

    async start({ commit, dispatch }, id) {
        commit("updatePendingRequestAction", { id, action: "start" });
        try {
            const response = await axios.post(`/api/productionrequests/${id}/start/`);
            await Promise.all([dispatch("fetchRequests"), dispatch("fetchStats")]);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePendingRequestAction", null);
        }
    },

    async confirm({ commit, dispatch }, { id, produced_quantity, comment, source_storage_location, destination_storage_location }) {
        commit("updatePendingRequestAction", { id, action: "confirm" });
        try {
            const response = await axios.post(
                `/api/productionrequests/${id}/confirm/`,
                { produced_quantity, comment: comment || "", source_storage_location, destination_storage_location }
            );
            await Promise.all([dispatch("fetchRequests"), dispatch("fetchStats")]);
            store.dispatch("session/fetchStats").catch(() => {});
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePendingRequestAction", null);
        }
    },

    async cancel({ commit, dispatch }, id) {
        commit("updatePendingRequestAction", { id, action: "cancel" });
        try {
            const response = await axios.post(`/api/productionrequests/${id}/cancel/`);
            await Promise.all([dispatch("fetchRequests"), dispatch("fetchStats")]);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePendingRequestAction", null);
        }
    },

    async reschedule({ commit, dispatch }, { id, planned_date }) {
        commit("updatePendingRequestAction", { id, action: "reschedule" });
        try {
            const response = await axios.post(
                `/api/productionrequests/${id}/reschedule/`,
                { planned_date }
            );
            await Promise.all([dispatch("fetchRequests"), dispatch("fetchStats")]);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePendingRequestAction", null);
        }
    },

    async updateRequest({ commit, dispatch }, { id, ...payload }) {
        commit("updatePendingRequestAction", { id, action: "update" });
        try {
            const response = await axios.patch(
                `/api/productionrequests/${id}/`,
                payload
            );
            await Promise.all([dispatch("fetchRequests"), dispatch("fetchStats")]);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePendingRequestAction", null);
        }
    },
};

const getters = {
    hasEstablishments: (state) => state.user_establishments.length > 0,
    singleEstablishment: (state) => state.user_establishments.length === 1,
    establishmentOptions: (state) => state.user_establishments,
    showEstablishmentBadge: (state) =>
        state.requests_filters.establishment === "all" &&
        state.user_establishments.length > 1,
    todoModeCount: (state) => state.stats.todo_count,
    doneProgressLabel: (state) => {
        const produced = state.stats.produced_units || 0;
        const planned = state.stats.planned_units_done || 0;
        if (!planned) {
            return produced;
        }
        return `${produced} / ${planned}`;
    },
    action_saving: (state) => state.pending_request_action !== null,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
